import { componentProvider } from 'js-component-framework/es';
import PopOver from '@/js/PopOver';
import storageAvailable from '@/js/utils/storageAvailable';
import './user-preferences.scss';

/**
 * UserPreferences component.
 */
class UserPreferences {
  /**
   * Start the component
   */
  constructor({ children, element, options }) {
    this.children = children;
    this.element = element;
    this.options = options;

    /**
     * The local storage key for the currency preference.
     * @type {String}
     */
    this.storageKey = 'currencyLocalization';

    if (storageAvailable('localStorage')) {
      this.init();
    }
  }

  /**
   * Set the stored currency preference.
   *
   * @param {string} currency The preferred currency; one of 'USD' or 'CAD'.
   */
  set preferredCurrency(currency) {
    window.localStorage.setItem(this.storageKey, currency);

    /**
     * The data attribute on the document root is used as a public API.
     * It's used by CSS to determine which price to display and by the
     * search JS components to use the correct currency when filtering
     * search results by price.
     */
    document.documentElement.dataset.currency = currency;
  }

  /**
   * Get the preferred currency, if any.
   *
   * @return {string|null}
   */
  get preferredCurrency() {
    return window.localStorage.getItem(this.storageKey);
  }

  /**
   * Sets the UserPreferences popover controller.
   */
  init = () => {
    const { button, currencyInputs, fieldset } = this.children;

    // Apply the preference on page load.
    if (this.preferredCurrency === 'CAD') {
      document.documentElement.dataset.currency = 'CAD';
    } else {
      this.preferredCurrency = 'USD';
    }

    // Ensure the radio buttons reflects the correct preference.
    currencyInputs.forEach((input) => {
      // eslint-disable-next-line no-param-reassign
      input.checked = (input.value === this.preferredCurrency);
    });

    // Set up the popover controller.
    this.button = new PopOver(button);

    fieldset.addEventListener('change', (event) => {
      const { target } = event;

      this.preferredCurrency = target.value;
    });
  };
}

const userPreferencesConfig = {
  name: 'user-preferences',
  component: UserPreferences,
  querySelector: {
    button: '.user-preferences__button',
    fieldset: 'fieldset',
  },
  querySelectorAll: {
    currencyInputs: '.user-preferences__input',
  },
};

componentProvider(userPreferencesConfig);
